/**
 * Abstract base class for PS Table data sources.
 *
 * PSTableDataSource provides a common interface for different types of data sources
 * that can be used with the PS Table component. This is an abstract class that
 * should be extended by specific data source implementations.
 *
 * @abstract
 * @class PSTableDataSource
 * @example
 * // Extend this class to create custom data sources
 * class CustomDataSource extends PSTableDataSource {
 *   constructor(options) {
 *     super(options);
 *     this.mode = 'Custom';
 *   }
 *
 *   async select() {
 *     // Implement data retrieval logic
 *     return await this.fetchData();
 *   }
 * }
 */
class PSTableDataSource {
	/**
	 * Creates a new PSTableDataSource instance.
	 *
	 * @param {Object} options - Data source configuration options
	 * @param {any} options.data_source - The underlying data source as sent to the ps-table component (array, API config, etc.)
	 * @param {PSTablePaging} options.paging - The pagination instance for the ps-table component
	 * @param {PSTableSorting} options.sorting - The sorting instance for the ps-table component
	 * @param {PSTableFiltering} options.filtering - The filtering instance for the ps-table component
	 */
	constructor(options) {
		options = options ?? {};

		this.data_source = options.data_source;
		this.paging = options.paging;
		this.sorting = options.sorting;
		this.filtering = options.filtering;

		/**
		 * The data_source type of this data source. This should be overridden by subclasses to indicate the specific type.
		 * @type {string}
		 */
		this.mode = 'Invalid';
	}

	/**
	 * Retrieves data from the data source.
	 *
	 * This method should be overridden by subclasses to implement
	 * the actual data retrieval logic. The base implementation
	 * returns an empty array.
	 *
	 * @abstract
	 * @returns {Promise<Array>} A promise that resolves to an array of records
	 */
	async select() {
		return [];
	}
}

export default PSTableDataSource;
